import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Logger } from '@bannerflow/sentinel-logger';
import { LibraryWidget } from '@domain/brand/brand-library';
import { EnvironmentService } from '@studio/common/services/environment.service';
import { catchError, firstValueFrom, map, of, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WidgetDataService {
    private http = inject(HttpClient);

    widgets: LibraryWidget[] = [];
    private request?: Promise<LibraryWidget | LibraryWidget[]>;
    private logger = new Logger('WidgetService');
    private bannerflowLibraryOrigin = inject(EnvironmentService).origins.bannerflowLibrary;
    private url = `${this.bannerflowLibraryOrigin}/api/widgets/`;

    async get(): Promise<LibraryWidget[]>;
    async get(id: string): Promise<LibraryWidget>;
    async get(id: string[]): Promise<LibraryWidget[]>;
    async get(sort: 'asc' | 'desc'): Promise<LibraryWidget[]>;
    async get(id: string, sort: 'asc' | 'desc'): Promise<LibraryWidget>;
    async get(id: string[], sort: 'asc' | 'desc'): Promise<LibraryWidget[]>;
    async get(
        id?: string | string[],
        sort: 'asc' | 'desc' = 'asc'
    ): Promise<LibraryWidget | LibraryWidget[]> {
        let requestUrl = `${this.url}?sort=${sort}`;

        await this.request;

        if (id) {
            const cachedWidget = this.widgets.find(widget => widget.id === id);

            if (cachedWidget) {
                return cachedWidget;
            }

            requestUrl = Array.isArray(id)
                ? `${this.url + id.join(',')}?sort=${sort}`
                : `${this.url + id}?sort=${sort}`;
        }

        this.request = firstValueFrom(
            this.http.get<LibraryWidget[]>(requestUrl).pipe(
                take(1),
                tap(libraryWidgets => {
                    this.widgets = libraryWidgets;
                }),
                map(libraryWidgets =>
                    id && libraryWidgets.length > 1 ? libraryWidgets[0] : libraryWidgets
                ),
                catchError(error => {
                    if (error instanceof HttpErrorResponse) {
                        if (error.status === 0) {
                            this.logger.error(
                                'Could not fetch widget. Network connection was most likely lost.',
                                error.message
                            );
                        } else {
                            this.logger.error('Could not fetch widget', error.message);
                        }
                    }

                    return of([]);
                })
            )
        );

        return await this.request;
    }

    post(widget: LibraryWidget): Promise<LibraryWidget> {
        return firstValueFrom(this.http.post<LibraryWidget>(this.url, widget));
    }

    put(widget: LibraryWidget): Promise<LibraryWidget> {
        return firstValueFrom(
            this.http.put<LibraryWidget>(this.url + widget.id, this.serialize(widget))
        );
    }

    private serialize(widget: LibraryWidget): LibraryWidget {
        const w = { ...widget };
        delete w.versionId;
        return w;
    }
}
